import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {HandlerOf, PredicateOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {CellsConversationsDragItem} from '../../../../helpers/conversationCellHelpers';
import {wrapActionCreator} from '../../../../helpers/reduxHelpers';
import {makeMapStateToProps} from '../../../../helpers/reselectHelpers';
import {DelegationModel} from '../../../../models/api/delegationModel';
import {CollapsedItemKeyPrefixesEnum} from '../../../../models/api/localPreferencesModel';
import {TeammateModel} from '../../../../models/api/teammateModel';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
  WorkspaceDisplayChevronActionsEnum,
  WorkspaceDisplayChevronSectionsEnum,
} from '../../../../services/tracking';
import {AppState} from '../../../../store/appState';
import {Dispatch} from '../../../../store/appStore';
import {getHasInternalDiscussions} from '../../../../store/data/company/companySelectors';
import {toggleExpandItemAction} from '../../../../store/data/localPreferences/localPreferencesActions';
import {getFindIsExpanded} from '../../../../store/data/localPreferences/localPreferencesSelectors';
import {
  canDropOnTeammateInboxTagAction,
  moveToTeammateInboxTagAction,
} from '../../../../store/data/teammates/teammatesActions';
import {DelegationWorkspaceInboxTag} from './delegationWorkspaceInboxTag';

/*
 * Constants.
 */

const defaultIsInboxTagSectionExpanded = false;

/*
 * Props.
 */

interface DelegationWorkspaceInboxTagContainerProps {
  owner: TeammateModel;
  delegation: DelegationModel;
}

/*
 * Selectors.
 */

function getOwner(_state: AppState, props: DelegationWorkspaceInboxTagContainerProps) {
  return props.owner;
}

function buildInboxTagSectionExpandedKey(teammateNamespace: string) {
  return `${CollapsedItemKeyPrefixesEnum.TEAMMATE_INBOX}${teammateNamespace}`;
}

/** Find the expanded state for our user. */
function makeGetIsExpanded() {
  return createSelector([getOwner, getFindIsExpanded], (owner, findIsExpanded) =>
    findIsExpanded(buildInboxTagSectionExpandedKey(owner.namespace), defaultIsInboxTagSectionExpanded),
  );
}

function makeGetOwnerStarredTagId() {
  return createSelector([getOwner], (owner) => owner.starredTagId);
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<DelegationWorkspaceInboxTagContainerProps>()(() => ({
  isExpanded: makeGetIsExpanded(),
  hasInternalDiscussions: getHasInternalDiscussions,
  ownerStarredTagId: makeGetOwnerStarredTagId(),
}));

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    toggleExpandItemAction: wrapActionCreator(dispatch, toggleExpandItemAction),
    canDropOnTeammateInboxTagAction: wrapActionCreator(dispatch, canDropOnTeammateInboxTagAction),
    moveToTeammateInboxTagAction: wrapActionCreator(dispatch, moveToTeammateInboxTagAction),
  };
}

export const DelegationWorkspaceInboxTagContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  (stateProps, dispatchProps, ownProps) => {
    const {owner} = ownProps;

    const onToggleExpandClick = () => {
      trackInterfaceEvent(InterfaceEventsEnum.P1_WORKSPACE_CHEVRON_CLICK, {
        location: WorkspaceDisplayChevronSectionsEnum.DELEGATED_INBOX,
        action: stateProps.isExpanded
          ? WorkspaceDisplayChevronActionsEnum.COLLAPSE
          : WorkspaceDisplayChevronActionsEnum.EXPAND,
      });
      dispatchProps.toggleExpandItemAction(
        buildInboxTagSectionExpandedKey(owner.namespace),
        defaultIsInboxTagSectionExpanded,
      );
    };

    const canConversationsDrop: PredicateOf<CellsConversationsDragItem> = (options) =>
      dispatchProps.canDropOnTeammateInboxTagAction(options, owner);

    const onConversationsDrop: HandlerOf<CellsConversationsDragItem> = (options) =>
      dispatchProps.moveToTeammateInboxTagAction(options, owner);

    return {
      ...ownProps,
      ...stateProps,
      onToggleExpandClick,
      canConversationsDrop,
      onConversationsDrop,
    };
  },
)(DelegationWorkspaceInboxTag);
